import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Ship, Globe, Truck, CheckCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

// Professional intermodal trucking images
const IMAGES = {
  importDrayage: 'https://images.unsplash.com/photo-1609330934087-5ff217016316?auto=format&fit=crop&w=800&q=80',
  exportLogistics: 'https://images.unsplash.com/photo-1766561994067-dbd575e1cff2?auto=format&fit=crop&w=800&q=80',
  localDrayage: 'https://images.unsplash.com/photo-1724525231969-173f10849bb1?auto=format&fit=crop&w=800&q=80',
  containerYard: 'https://images.unsplash.com/photo-1611816951577-198c5dcf0e96?auto=format&fit=crop&w=1920&q=80',
  craneLoading: 'https://images.unsplash.com/photo-1770710195322-0ef5befa9d40?auto=format&fit=crop&w=800&q=80'
};

export default function Services() {
  return (
    <div className="min-h-screen bg-[#0A0E1A]">
      <Navbar />
      
      {/* Hero Section with Background */}
      <section className="relative py-20 px-6 overflow-hidden">
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url('${IMAGES.containerYard}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0E1A] via-[#0A0E1A]/90 to-[#0A0E1A]" />
        
        <div className="relative z-10 max-w-6xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold tracking-tighter uppercase mb-6">
            <span className="text-white">OUR </span>
            <span className="text-[#00A6E8]">SERVICES</span>
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Professional intermodal container drayage services serving Columbus, Ohio and beyond.
          </p>
        </div>
      </section>
      
      <section className="py-12 px-6" data-testid="services-section">
        <div className="max-w-6xl mx-auto space-y-16">
          
          {/* Service 1: Import Drayage */}
          <div className="bg-[#0F172A] border border-white/10 rounded-lg overflow-hidden" data-testid="service-import">
            <div className="grid md:grid-cols-2 gap-0">
              {/* Image */}
              <div className="relative h-64 md:h-auto overflow-hidden">
                <img 
                  src={IMAGES.importDrayage}
                  alt="Container pickup from rail yard"
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0F172A] md:block hidden" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] to-transparent md:hidden" />
              </div>
              
              {/* Content */}
              <div className="p-8 md:p-12 flex flex-col justify-center">
                <div className="flex items-center gap-4 mb-6">
                  <div className="p-3 bg-[#00A6E8]/10 rounded-lg">
                    <Ship className="text-[#00A6E8]" size={32} />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold uppercase tracking-tight text-white">Intermodal Import Drayage</h2>
                </div>
                
                <p className="text-slate-400 mb-6">
                  Complete import container services from rail ramp to final destination. We handle everything from DO processing to chassis coordination.
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="text-[#00A6E8] mt-0.5 flex-shrink-0" size={18} />
                    <div>
                      <p className="text-white font-semibold text-sm">Rail Ramp Pickup</p>
                      <p className="text-slate-500 text-xs">CSX & Norfolk Southern terminals</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <CheckCircle className="text-[#00A6E8] mt-0.5 flex-shrink-0" size={18} />
                    <div>
                      <p className="text-white font-semibold text-sm">Delivery Order Processing</p>
                      <p className="text-slate-500 text-xs">Complete DO documentation</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <CheckCircle className="text-[#00A6E8] mt-0.5 flex-shrink-0" size={18} />
                    <div>
                      <p className="text-white font-semibold text-sm">Chassis Coordination</p>
                      <p className="text-slate-500 text-xs">Rental & split management</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <CheckCircle className="text-[#00A6E8] mt-0.5 flex-shrink-0" size={18} />
                    <div>
                      <p className="text-white font-semibold text-sm">Storage & Prepull</p>
                      <p className="text-slate-500 text-xs">Secure container storage</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Service 2: Export Logistics */}
          <div className="bg-[#0F172A] border border-white/10 rounded-lg overflow-hidden" data-testid="service-export">
            <div className="grid md:grid-cols-2 gap-0">
              {/* Content */}
              <div className="p-8 md:p-12 flex flex-col justify-center order-2 md:order-1">
                <div className="flex items-center gap-4 mb-6">
                  <div className="p-3 bg-[#FFA726]/10 rounded-lg">
                    <Globe className="text-[#FFA726]" size={32} />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold uppercase tracking-tight text-white">Export Container Logistics</h2>
                </div>
                
                <p className="text-slate-400 mb-6">
                  Full export container services from shipper pickup to rail ramp delivery. We ensure your cargo meets all cutoff deadlines.
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="text-[#FFA726] mt-0.5 flex-shrink-0" size={18} />
                    <div>
                      <p className="text-white font-semibold text-sm">Empty Container Pickup</p>
                      <p className="text-slate-500 text-xs">From designated depots</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <CheckCircle className="text-[#FFA726] mt-0.5 flex-shrink-0" size={18} />
                    <div>
                      <p className="text-white font-semibold text-sm">Shipper Coordination</p>
                      <p className="text-slate-500 text-xs">Direct scheduling</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <CheckCircle className="text-[#FFA726] mt-0.5 flex-shrink-0" size={18} />
                    <div>
                      <p className="text-white font-semibold text-sm">Cutoff Management</p>
                      <p className="text-slate-500 text-xs">Rail & port deadlines</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <CheckCircle className="text-[#FFA726] mt-0.5 flex-shrink-0" size={18} />
                    <div>
                      <p className="text-white font-semibold text-sm">Documentation Intake</p>
                      <p className="text-slate-500 text-xs">Export paperwork processing</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Image */}
              <div className="relative h-64 md:h-auto overflow-hidden order-1 md:order-2">
                <img 
                  src={IMAGES.exportLogistics}
                  alt="Truck hauling loaded container"
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[#0F172A] md:block hidden" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] to-transparent md:hidden" />
              </div>
            </div>
          </div>
          
          {/* Service 3: Local Drayage */}
          <div className="bg-[#0F172A] border border-white/10 rounded-lg overflow-hidden" data-testid="service-local">
            <div className="grid md:grid-cols-2 gap-0">
              {/* Image */}
              <div className="relative h-64 md:h-auto overflow-hidden">
                <img 
                  src={IMAGES.localDrayage}
                  alt="Container chassis yard operations"
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0F172A] md:block hidden" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] to-transparent md:hidden" />
              </div>
              
              {/* Content */}
              <div className="p-8 md:p-12 flex flex-col justify-center">
                <div className="flex items-center gap-4 mb-6">
                  <div className="p-3 bg-white/10 rounded-lg">
                    <Truck className="text-white" size={32} />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold uppercase tracking-tight text-white">Dedicated Columbus Local Drayage</h2>
                </div>
                
                <p className="text-slate-400 mb-6">
                  Structured rate agreements and transparent accessorial policies for consistent Columbus-area logistics operations. 
                  Serving all major rail ramps and depots in the Columbus intermodal market.
                </p>
                
                <div className="bg-[#1E293B] rounded-lg p-4 mb-6">
                  <p className="text-white font-semibold mb-2">Serviced Locations</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-[#00A6E8]/20 text-[#00A6E8] text-sm rounded">CSX Columbus</span>
                    <span className="px-3 py-1 bg-[#FFA726]/20 text-[#FFA726] text-sm rounded">Norfolk Southern</span>
                    <span className="px-3 py-1 bg-white/10 text-white text-sm rounded">All Local Depots</span>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="text-white flex-shrink-0" size={18} />
                    <p className="text-slate-300 text-sm">Zone-based pricing</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="text-white flex-shrink-0" size={18} />
                    <p className="text-slate-300 text-sm">Transparent rates</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="text-white flex-shrink-0" size={18} />
                    <p className="text-slate-300 text-sm">All container sizes</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="text-white flex-shrink-0" size={18} />
                    <p className="text-slate-300 text-sm">Chassis available</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold uppercase tracking-tight text-white mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-slate-400 mb-8">
            Submit a work order or get an instant rate estimate for your container drayage needs.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/register">
              <Button className="bg-[#00A6E8] text-[#0A0E1A] font-bold uppercase tracking-wider hover:bg-[#33B8ED] px-8 py-6">
                Submit Work Order
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
            <Link to="/rate-calculator">
              <Button variant="outline" className="border-[#FFA726] text-[#FFA726] font-bold uppercase tracking-wider hover:bg-[#FFA726]/10 px-8 py-6">
                Rate Calculator
              </Button>
            </Link>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}
